import React, { useState } from 'react';
import { Poppins, StyledAvatar, TableSkeleton } from 'src/common';
import { getProfileCredentials } from 'src/utils/auth';
import { useTheme } from 'styled-components';
import * as S from './OrgAccess.styles';
import AccessTable from './comps/AccessTable';
import { MemberAccess, useFlatOrganisationsUserLazyQuery } from 'src/graphql/_generated';
import { FlatOrgUserData } from 'src/utils/treeNode';
import { SelectedOrgUnit } from '../types';

interface OrgAccessProps {
  targetUserEmail: string | null;
  isOwnProfile: boolean;
  selectedOrgUnit?: SelectedOrgUnit;
  onChange: (selected: SelectedOrgUnit | null) => void;
  closeModal: () => void;
  targetUserName?: string;
}

export const OrgAccess: React.FC<OrgAccessProps> = ({
  targetUserEmail,
  isOwnProfile,
  selectedOrgUnit,
  onChange,
  closeModal,
  targetUserName,
}) => {
  const colors = useTheme().colors;
  const [anchorEl, setAnchorEl] = useState<null | HTMLDivElement>(null);

  const [loadFlatOrgs, { data: flatOrganisationsData, loading: flatOrgsLoading }] = useFlatOrganisationsUserLazyQuery();

  const flatOrganisations = flatOrganisationsData?.flatOrganisations || [];

  const handleRowSelect = (selected: FlatOrgUserData | null) => {
    onChange(selected);
    handleClose();
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(e.currentTarget);
    e.stopPropagation();
    if (!flatOrganisationsData && targetUserEmail) {
      loadFlatOrgs({ variables: { memberEmail: targetUserEmail } });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const isOpen = Boolean(anchorEl);

  return (
    <S.Div>
      <div className="seletor" onClick={handleClick}>
        {selectedOrgUnit ? (
          <>
            <StyledAvatar src={''} $gradientBlue={selectedOrgUnit.recordType === 'W'} $xsmall>
              {getProfileCredentials(selectedOrgUnit.name)}
            </StyledAvatar>{' '}
            <Poppins weight={500} px={14} color="prussianBlue">
              {selectedOrgUnit.name}
            </Poppins>
          </>
        ) : (
          <>
            <Poppins weight={500} px={14} color="prussianBlue">
              [GLOBAL]
            </Poppins>
          </>
        )}
        <S.ChevronSvg
          $open={isOpen}
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="7"
          viewBox="0 0 12 7"
          fill="none"
        >
          <path
            d="M10.834 0.833007L5.83398 5.83301L0.833984 0.833008"
            stroke={colors.prussianBlue}
            strokeWidth="1.66667"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </S.ChevronSvg>
      </div>

      <S.StyledMenu
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            style: {
              marginTop: '12px',
            },
          },
        }}
        autoFocus={false}
        disableAutoFocusItem
        anchorEl={anchorEl}
        open={isOpen}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            handleClose();
          }
        }}
      >
        <div className="org-access-menu">
          {flatOrgsLoading || !flatOrganisationsData ? (
            <S.SkeletonWrap>
              <TableSkeleton rowHeight={32} />
            </S.SkeletonWrap>
          ) : (
            flatOrganisations.length > 0 && (
              <AccessTable
                data={flatOrganisations}
                selectedId={selectedOrgUnit?.id}
                onRowSelect={handleRowSelect}
                isOwnProfile={isOwnProfile}
                hasUnitSelected={!!selectedOrgUnit}
                onViewGlobal={() => {
                  onChange(null);
                  handleClose();
                }}
                closeModal={closeModal}
                targetUserName={targetUserName}
                targetUserEmail={targetUserEmail || undefined}
                isRowDisabled={(item) => {
                  const allowedAccess = [MemberAccess.Yes];
                  return !allowedAccess.includes(item.member?.access as MemberAccess);
                }}
              />
            )
          )}
        </div>
      </S.StyledMenu>
    </S.Div>
  );
};
